import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, Pencil, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import meetHubLogo from "@/assets/meethub-logo.png";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EditProfileDialog } from "@/components/EditProfileDialog";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

interface UserProfile {
  display_name: string;
  avatar_url: string | null;
}

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const navItems = [
    { name: "Home", path: "/" },
    { name: "Features", path: "/features" },
    { name: "Pricing", path: "/pricing" },
    { name: "Security", path: "/security" },
    { name: "Resources", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const isMarketingPage =
    !location.pathname.startsWith("/dashboard") &&
    !location.pathname.startsWith("/meeting") &&
    !location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith("/control-panel");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) toast.error("Failed to log out");
    else {
      toast.success("Logged out successfully");
      navigate("/");
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", user.id)
        .maybeSingle();
      setProfile(data);
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`profile-updates-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const next = (payload.new || payload.old) as UserProfile | null;
          if (next) setProfile({ display_name: next.display_name, avatar_url: next.avatar_url });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const getUserInitials = () => {
    const name = profile?.display_name || user?.user_metadata?.display_name || user?.email;
    return name ? name.charAt(0).toUpperCase() : "U";
  };
  const getDisplayName = () => {
    if (profile?.display_name) return profile.display_name;
    if (user?.user_metadata?.display_name) return user.user_metadata.display_name;
    return user?.email?.split("@")[0] || "";
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "border-border/80 bg-background/90 shadow-md backdrop-blur-xl"
          : "border-primary/10 bg-[linear-gradient(180deg,hsl(var(--background)/0.96),hsl(var(--secondary)/0.9))] shadow-sm backdrop-blur-lg"
      }`}
    >
      {isMarketingPage && !user && (
        <div className="hidden md:block border-b border-border/70 bg-secondary/40">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs">
            <p className="text-muted-foreground">
              Instant browser meetings for teams, freelancers, and startups.
            </p>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                Compare plans
              </Link>
              <Link to="/security" className="text-muted-foreground hover:text-foreground transition-colors">
                Security
              </Link>
            </div>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="p-1.5 rounded-lg bg-gradient-primary shadow-sm">
              <img src={meetHubLogo} alt="MeetHub logo" className="h-7 w-auto" loading="eager" />
            </div>
            <span className="text-lg font-semibold tracking-tight">MeetHub</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => {
              const active = item.path === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors relative ${
                    active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {item.name}
                  {active && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                </Link>
              );
            })}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-2">
            <LanguageSelector />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-1 pr-2 rounded-md transition-colors hover:bg-accent">
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={profile?.avatar_url || user.user_metadata?.avatar_url} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium max-w-24 truncate">{getDisplayName()}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      {t("nav.dashboard")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="flex items-center gap-2 cursor-pointer"
                    onSelect={(e) => {
                      e.preventDefault();
                      setProfileDialogOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                    {t("nav.editProfile")}
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/developer-apps" className="flex items-center gap-2 cursor-pointer">
                      <Code className="h-4 w-4" />
                      {t("nav.developerApps", "Developer Apps")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                  >
                    <LogOut className="h-4 w-4" />
                    {t("nav.logOut")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">Sign in</Button>
                </Link>
                <Link to="/join">
                  <Button variant="outline" size="sm">Join meeting</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="shadow-sm">Start free trial</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 animate-in slide-in-from-top">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-2 text-sm font-medium ${
                  location.pathname === item.path ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {item.name}
              </Link>
            ))}
            <div className="flex flex-col gap-2 pt-3 border-t border-border">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                    <button className="w-full justify-start gap-2 inline-flex items-center text-sm font-medium px-3 py-2 rounded-md hover:bg-accent">
                      <User className="h-4 w-4" /> Dashboard
                    </button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-destructive hover:text-destructive"
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut className="h-4 w-4" /> Log out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full">Sign in</Button>
                  </Link>
                  <Link to="/join" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="outline" size="sm" className="w-full">Join meeting</Button>
                  </Link>
                  <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                    <Button size="sm" className="w-full">Start free trial</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <EditProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
    </nav>
  );
};

